import React from 'react';
import {
    FlexFitCenter,
    FormContainer,
    GreyTypography,
    MainContent,
    PageTitle
} from '@/app/[locale]/(welcome)/_components';
import { useTranslations } from 'next-intl';
import { Flex4XS, FlexColMD, FlexColSM } from '@/components/Flex';
import styled from 'styled-components';
import { Loader } from '@/components/core/Loader';
import { useTimer } from 'react-timer-hook';
import OtpInput from '@/components/core/OtpInput/OtpInput';
import Typography from '@/components/core/Typography/Typography';
import { device } from '@/components/Media';

const SubtitleTypography = styled(GreyTypography)`
    text-align: center;
    white-space: pre;
    
    @media screen and ${device.laptopS} {
        white-space: unset;
    }
`;

const SubtitleInfoBlock = styled(FlexColSM)`
    @media screen and ${device.laptopS} {
        gap:  ${({ theme }) => theme.spacing['3xs']};
    }
`;

const LinkTypography = styled(Typography)<{
    $disabled?: boolean;
}>`
    color: ${({ theme, $disabled }) => $disabled ? theme.colors.palette.grey_600 : theme.colors.palette.blue_500};
    cursor: ${({ $disabled }) => $disabled ? 'default' : 'pointer'};
`;

const ActionsWrapper = styled(Flex4XS)`
    @media screen and ${device.laptopS} {
        align-items: center;
        flex-direction: column;
        gap:  ${({ theme }) => theme.spacing['3xs']};
    }
`;

const CenteredFlexColMD = styled(FlexColMD)`
    align-items: center;
    
    @media screen and ${device.laptopS} {
        gap:  ${({ theme }) => theme.spacing['3xs']};
    }
`;

export function VerifyEmailForm({
    loading,
    handleConfirm,
    handleResend,
    otpError,
    goBack,
    email,
    clearError
}) {
    const t = useTranslations();

    const timer = useTimer({
        // eslint-disable-next-line
        expiryTimestamp: new Date(+new Date(Date.now() + 60_000)),
        autoStart: true
    });

    const handleResendClick = () => {
        if (timer.isRunning) {
            return;
        }

        handleResend();
        timer.restart(new Date(+new Date(Date.now() + 60_000)));
    };

    return (
        <MainContent>
            <PageTitle>
                {t('auth.verifyEmailTitle')}
            </PageTitle>

            {loading ? (
                <FlexFitCenter>
                    <Loader size='lg' variant='grey' />
                </FlexFitCenter>
            ) : (
                <FormContainer>
                    <CenteredFlexColMD>
                        <SubtitleTypography variant='uiType' size='sm' modifier='R'>
                            {t.rich('auth.verifyEmailSubtitle', {
                                email,
                                br: () => <br />
                            })}
                        </SubtitleTypography>

                        <SubtitleInfoBlock>
                            <OtpInput
                              errored={otpError}
                              hint={otpError ? `${t('validation.invalidOTP')}. ${t('validation.pleaseTryAgain')}` : ''}
                              onSubmit={handleConfirm}
                              clearError={clearError}
                            />

                            <ActionsWrapper>
                                <GreyTypography
                                  variant='uiType'
                                  size='sm'
                                  modifier='R'
                                >{t('auth.didntGetOtp')}
                                </GreyTypography>
                                <LinkTypography
                                  onClick={goBack}
                                  variant='uiType'
                                  size='sm'
                                  modifier='S'
                                >{t('auth.updateEmail')}
                                </LinkTypography>
                                <GreyTypography variant='uiType' size='sm' modifier='R'>{t('common.or')}</GreyTypography>
                                <LinkTypography
                                  onClick={handleResendClick}
                                  $disabled={timer.isRunning}
                                  variant='uiType'
                                  size='sm'
                                  modifier='S'
                                >{t('auth.sendCodeAgain') + (timer.isRunning ? ` (${String(timer.minutes).padStart(2, '0')}:${String(timer.seconds).padStart(2, '0')})` : '')}
                                </LinkTypography>
                            </ActionsWrapper>
                        </SubtitleInfoBlock>
                    </CenteredFlexColMD>
                </FormContainer>
            )}
        </MainContent>
    );
}
